import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Products2Hero from '../components/Products2Hero';
import { MapPin, Phone, Clock, Target, Lightbulb, Wrench, Send, Navigation, Mail } from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './StorePage.css';

gsap.registerPlugin(ScrollTrigger);

const StorePage = () => {
  const sectionRef = useRef(null);
  const infoRef = useRef(null);
  const helpRef = useRef(null);
  const [headingVisible, setHeadingVisible] = useState(false);
  const headingRef = useRef(null);

  const storeAddress = "X Road, opp. Azampura, Chaderghat, Malakpet, Hyderabad, Telangana 500024";

  const storeMarquee = [
    'Visit Us Today',
    'Certified Tech Experts',
    'Open 6 Days a Week',
    'Premium Demo Units',
    'On-Site Repairs',
    'Same-Day Pickup',
    'Malakpet Hyderabad',
    'Expert Consultations'
  ];

  const splitWords = (text, startDelay = 0) => {
    const words = text.trim().split(/\s+/);
    return words.map((word, i) => (
      <span
        key={`${word}-${i}`}
        className="word-animate"
        style={{ animationDelay: `${startDelay + i * 40}ms` }}
      >
        {word}
        {i < words.length - 1 ? ' ' : ''}
      </span>
    ));
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.remove('aos-hidden');
            entry.target.classList.add('aos-visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    // Select all aos-items within the whole page container
    const items = document.querySelectorAll('.aos-item');
    items.forEach((el) => observer.observe(el));
    
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHeadingVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (headingRef.current) observer.observe(headingRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Info Cards Animation
      if (infoRef.current) {
        gsap.fromTo('.info-card', { y: 60, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.8, stagger: 0.2, ease: 'power3.out',
          scrollTrigger: { trigger: infoRef.current, start: 'top 80%' },
        });
      }
      
      // Benefit Cards Animation
      if (sectionRef.current) {
        gsap.fromTo('.benefit-card', { y: 60, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: 'power3.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' },
        });
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <div className="store-page">
      <Helmet>
        <title>Visit Our Hyderabad Store | Best Price Refurbished Laptops | Nexawave</title>
        <meta name="description" content="Visit Nexawave Technology's flagship store in Hyderabad. Test certified refurbished laptops, get expert advice, and find the best deals on affordable used tech." />
        <meta name="keywords" content="laptop store hyderabad, refurbished laptops malakpet, used laptops near me, nexawave store, best price laptops hyderabad" />
      </Helmet>
      
      <Products2Hero 
        className="hero-tight"
        tagline="FLAGSHIP EXPERIENCE · VISIT OUR HYDERABAD STORE · PREMIUM TECH SUPPORT"
        title1="Our Tech Store"
        title2=""
        title3=""
        description=""
        button1Text="Get Directions"
        button1Link="#map"
        button2Text="Contact Us"
        button2Link="/contact-us"
        marqueeItems={storeMarquee}
      />

      <div className="store-content">
        {/* Store Info Section - Re-designed like About Us Ethos */}
        <section ref={infoRef} className="pt-8 pb-20 px-6 md:px-10 max-w-7xl mx-auto store-info-section">
          <div className="flex items-center gap-4 mb-4">
            <span className="pill">Store Locator</span>
            <div className="h-px bg-blue-accent/20 flex-grow" />
          </div>

          <h2 
            ref={headingRef}
            className={`section-fade ${headingVisible ? 'visible' : ''} text-4xl md:text-6xl font-black leading-[1.1] tracking-tight text-obsidian mb-8`}
          >
            {splitWords('Where Innovation Meets', 100)}
            <br />
            <em className="text-blue-accent italic font-normal" style={{ fontStyle: 'normal' }}>
              {splitWords('The Hands-On Reach.', 220)}
            </em>
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="info-card group">
              <div className="info-icon-wrapper">
                <MapPin className="text-blue-accent" size={24} />
              </div>
              <div className="info-text">
                <h3>Our Address</h3>
                <p className="address-text">{storeAddress}</p>
                <a href="#map" className="inline-link mt-2">
                  <Navigation size={14} className="mr-1" /> View Map
                </a>
              </div>
            </div>

            <div className="info-card group">
              <div className="info-icon-wrapper">
                <Phone className="text-blue-accent" size={24} />
              </div>
              <div className="info-text">
                <h3>Contact Details</h3>
                <p>Phone: +91 8688090217</p>
                <p>Email: info@nexawave.com</p>
                <a href="mailto:info@nexawave.com" className="inline-link mt-2">
                  <Mail size={14} className="mr-1" /> Send Email
                </a>
              </div>
            </div>

            <div className="info-card group highlight">
              <div className="info-icon-wrapper accent">
                <Clock className="text-white" size={24} />
              </div>
              <div className="info-text">
                <h3>Store Hours</h3>
                <p>Mon - Sat: 10:00 AM - 8:00 PM</p>
                <p>Sunday: 11:00 AM - 6:00 PM</p>
                <span className="status-badge mt-2">Open Now</span>
              </div>
            </div>
          </div>
        </section>

        {/* Map & Visual Section */}
        <section id="map" className="store-map-section aos-item aos-hidden">
          <div className="map-wrapper rounded-3xl overflow-hidden border border-blue-accent/10 shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.2773326782707!2d78.4919386288359!3d17.37733266782707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDIyJzM4LjQiTiA3OMKwMjknMzEuMCJF!5e0!3m2!1sen!2sin!4v1234567890123!5m2!1sen!2sin"
              width="100%"
              height="550"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Store Location Map"
              className="map-iframe"
            ></iframe>
            <div className="map-overlay p-8 hidden md:block">
              <div className="glass-panel p-6 rounded-2xl max-w-sm">
                <h4 className="font-bold text-lg mb-2">Finding Us Made Easy</h4>
                <p className="text-sm text-zinc-text-dark leading-relaxed">
                  Located in the heart of Malakpet, Nexawave is easily accessible via public transport and has ample parking space.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section - Re-designed like Process Section */}
        <section ref={sectionRef} className="pt-12 pb-24 px-6 md:px-10 max-w-7xl mx-auto store-experience">
          <div className="flex flex-col items-center text-center mb-16">
            <span className="pill mb-4">The Nexawave Difference</span>
            <h2 className="text-3xl md:text-5xl font-black text-obsidian tracking-tight">
              Why Visit <span className="text-blue-accent">Our Flagship?</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: <Target size={22} />, title: 'Hands-On Experience', desc: 'Touch, feel, and test various models to find your perfect fit.' },
              { icon: <Lightbulb size={22} />, title: 'Expert Consultations', desc: 'Get personalized tech advice from our on-site certified engineers.' },
              { icon: <Wrench size={22} />, title: 'On-Site Testing', desc: 'Perform any benchmarks or stress tests to verify performance.' },
              { icon: <Send size={22} />, title: 'Instant Fulfillment', desc: 'Walk in, pick your tech, and take it home the same day.' }
            ].map((benefit, i) => (
              <div key={i} className="benefit-card">
                <div className="benefit-icon-box">
                  {benefit.icon}
                </div>
                <h3 className="font-bold text-lg mb-3 text-obsidian">{benefit.title}</h3>
                <p className="text-sm text-zinc-text-dark leading-relaxed">{benefit.desc}</p>
                <div className="hover-line" />
              </div>
            ))}
          </div>
        </section>

      </div>
    </div>
  );
};

export default StorePage;

